import { useCallback, useEffect, useState } from 'react'
import { t } from 'i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import filterSetApi from 'api/ApiActions/Reporting/Overview/FilterSet'
import {
  updateFilter,
  setFilters,
  setSavedSelectionFilters,
  setSavedFilterName,
  resetFilters,
  setFilterVisibility,
} from 'redux/slices/FilterBuilder'
import { setReportingDetails } from 'redux/slices/ShareReportingDetails'
import { useAlert } from 'components/Shared/Alert'
import { RANGE_TYPES } from 'lib/GlobalConstants'
import { trackActions } from 'containers/Helper'
import { getDateRange, calculateDateRange } from 'lib/DateHelper'
import sharedDetailsApi from 'api/ApiActions/Reporting/Overview/ShareDashboard'

interface SavedFilterProps {
  filterId?: string
  filterName?: string
}

interface SaveApiResponseParams {
  filterName: string
  selectedFilters: any[] // Need to replace `any` with a proper type if available
  selectedIndex: number
  savedFilterProps?: SavedFilterProps
  filterSetResult?: any
}

const useFilterSet = () => {
  const dispatch = useAppDispatch()

  const {
    site: { site: { createdAt = '' } = {} } = {},
    features: { features: currentFeatures },
    filterBuilder,
  } = useAppSelector((state) => state)
  const { showAlert } = useAlert()
  const [filterSetResult, setFilterResult] = useState<any>()
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [isFilterSaved, setIsFilterSaved] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)

  /** get list for saved filter */
  const fetchSavedFilters = async () => {
    const response = await filterSetApi.getFilterSet()

    await setFilterResult(response) // need to change this
    dispatch(setFilterVisibility(false))
  }

  const handleMenuItemClick = async (event: any, index: number) => {
    await setSelectedIndex(index)
    if (index !== -1) {
      const { id: filterId, filterName } = filterSetResult?.[index] || {}
      dispatch(setSavedSelectionFilters([]))
      onSavedFilterChange?.(filterId)
      dispatch(setSavedFilterName({ filterName, filterId }))
    }
  }

  const onClearFilterSelection = async (isDelete = false) => {
    !isDelete &&
      trackActions(`Clicked on clear all`, {
        featureName: 'Overview dashboard filters',
        sectionName: 'Overview (Home) Dashboard page',
        ...currentFeatures?.['segment_analytics_admin']?.enabled,
      })

    handleMenuItemClick(undefined, -1)
    const response = await sharedDetailsApi.getSharedData()

    const dateRange = getDateRange('last_7_days', false)
    const calculatedRange = calculateDateRange('last_7_days', dateRange)

    const item = {
      dateRange: dateRange,
      calculatedRange,
      dateRangeOption: 'last_7_days',
    }
    dispatch(updateFilter({ item, index: 0 }))
    dispatch(setSavedSelectionFilters([]))
    dispatch(
      setSavedFilterName({
        originalFilterName: '',
        filterName: '',
        filterId: '',
      }),
    )

    if (response?.response?.status === 404) {
      dispatch(setReportingDetails({ link: '', id: '', password: '' }))
    }
    if (response?.id) {
      const { id = '', link = '', password = '' } = response || {}
      dispatch(setReportingDetails({ link, id, password }))
    }
  }

  const handleDeleteSelection = useCallback(async (filterId: string) => {
    const { data: title } = await filterSetApi.deleteFilterSet(filterId)

    showAlert({
      type: 'success',
      title,
      isHTML: true,
    })

    await dispatch(resetFilters())

    await onClearFilterSelection(true)
    await fetchSavedFilters()
    return !!title
  }, [])

  const handleRenameSelection = useCallback(
    async (filterId: string, filterName: string, inputRef: any) => {
      const response = await filterSetApi.renameFilterSet({
        filterId,
        filterName,
      })

      if (response?.response?.status === 422) {
        const { response: { data: validation = '' } = {} } =
          (await response) || {}

        inputRef?.current?.updateValue({
          isError: true,
          text: filterName,
          validation,
          position: 'left',
        })
        return false
      } else {
        const { message: title } = response
        await fetchSavedFilters()
        dispatch(
          setSavedFilterName({
            originalFilterName: filterName,
            filterName,
            filterId,
          }),
        )
        title &&
          showAlert({
            type: 'success',
            title,
            isHTML: true,
          })
        return !!title
      }
    },
    [],
  )

  const onSavedFilterChange = useCallback(
    async (id: number) => {
      const {
        filterParams,
        sharedUrl,
        id: shedUrlId,
      } = await filterSetApi.showFilterSet(id)

      const shredUrlData = { ...sharedUrl, shedUrlId }
      const dateFilter: string = filterParams[0]?.dateRangeOption
      const dateRange: any = filterParams[0]?.dateRange
      const updatedDateRange =
        dateFilter !== RANGE_TYPES.custom && createdAt
          ? getDateRange(dateFilter, false, createdAt)
          : dateRange
      const calculatedRange =
        dateFilter === 'all_time'
          ? t(`filters.dateRange.ranges.${RANGE_TYPES.allTime}`)
          : calculateDateRange(dateFilter, updatedDateRange)

      const item = {
        dateRange: {
          ...updatedDateRange,
        },
        calculatedRange,
      }

      dispatch(setFilters(filterParams))
      dispatch(
        updateFilter({
          item,
          index: 0,
        }),
      )
      dispatch(setSavedSelectionFilters(filterParams))
      dispatch(setFilterVisibility(false))
      dispatch(setReportingDetails({ ...shredUrlData }))
    },
    [createdAt],
  )

  const getSaveApiResponse = useCallback(
    async ({
      filterName,
      selectedFilters,
      selectedIndex,
      savedFilterProps = {},
    }: SaveApiResponseParams) => {
      let response: any = []
      const { createFilterSet, updateFilterSet } = filterSetApi || {}

      const isNonselectedIndex = selectedIndex === -1
      const removeNewLine = (name: string) => name?.split('\n')?.join('')
      const trimmedName = (name: string) =>
        removeNewLine(name?.toLowerCase()?.trim())
      const nonEmptyFilters = selectedFilters.filter((item: any) => {
        return (
          item.name === 'dateRange' ||
          (Object.prototype.hasOwnProperty.call(item, 'value') &&
            !!item.value.length)
        )
      })

      const isSameName =
        trimmedName(filterName) !==
        trimmedName(savedFilterProps?.filterName ?? '')

      if (isNonselectedIndex || (!isNonselectedIndex && isSameName)) {
        response = await createFilterSet({
          filter_name: filterName,
          filters: nonEmptyFilters,
        })

        await fetchSavedFilters()
      } else {
        response = await updateFilterSet({
          filterId: savedFilterProps?.filterId ?? '',
          filter_name: filterName,
          filters: nonEmptyFilters,
        })

        setIsUpdated(true)
      }
      return response
    },
    [selectedIndex, filterSetResult],
  )

  /** call on saving button click */
  const onSaveFilterSet = useCallback(
    async (
      inputRef: React.RefObject<any>,
      selectedFilters: any[],
      savedFilterProps?: SavedFilterProps,
    ) => {
      setIsFilterSaved(false)
      const {
        textValue: { text = '', isError = false, validation = '' } = {},
        updateValue,
      } = inputRef?.current || {}
      const filterName = text?.trim()
      const validationCheck = validation === t('reporting.filterSet.charLimit')

      if ((isError && validationCheck) || (!isError && !validationCheck)) {
        const response =
          (await getSaveApiResponse({
            filterName,
            selectedFilters,
            filterSetResult,
            selectedIndex,
            savedFilterProps,
          })) || {}

        if (response?.response?.status === 422) {
          const { response: { data: validation = '' } = {} } =
            (await response) || {}

          updateValue({
            isError: true,
            text: filterName,
            validation,
            position: 'left',
          })
          return false
        } else {
          const { message: title, sharedUrl, id: shedUrlId } = await response

          await dispatch(setReportingDetails({ ...sharedUrl, shedUrlId }))
          showAlert({
            type: 'success',
            title,
            isHTML: true,
          })

          await dispatch(setSavedSelectionFilters(selectedFilters))
          dispatch(
            setSavedFilterName({
              filterName,
              originalFilterName: filterName,
              filterId: shedUrlId,
            }),
          )

          setIsFilterSaved(true)
          setIsUpdated(false)
          return true
        }
      } else {
        return false
      }
    },
    [
      selectedIndex,
      isUpdated,
      filterSetResult,
      filterBuilder,
      getSaveApiResponse,
    ],
  )

  // on load call the list api and on route change reset savedFilters
  useEffect(() => {
    fetchSavedFilters()
    return () => {
      dispatch(setSavedSelectionFilters([]))
    }
  }, [])

  useEffect(() => {
    const filterResultLength = filterSetResult?.length

    isFilterSaved && !isUpdated && setSelectedIndex(filterResultLength - 1)
  }, [isFilterSaved])

  return {
    onSaveFilterSet,
    onSavedFilterChange,
    filterSetResult,
    handleMenuItemClick,
    handleRenameSelection,
    selectedIndex,
    handleDeleteSelection,
    onClearFilterSelection,
  }
}
export default useFilterSet
